import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Newspaper, ChevronRight, AlertCircle } from "lucide-react";

const CATEGORY_ORDER = ["BREAKING NEWS", "TAP IN NEWS", "WEATHER", "MUSIC", "SPORTS", "STREAMING", "CULTURE", "EXCLUSIVE"];
const CATEGORY_TINT = {
  "BREAKING NEWS": "bg-red-600 text-white",
  "TAP IN NEWS": "bg-tv-accent text-black",
  "WEATHER": "bg-sky-400 text-black",
  "MUSIC": "bg-fuchsia-500 text-white",
  "SPORTS": "bg-emerald-500 text-black",
  "STREAMING": "bg-indigo-400 text-black",
  "CULTURE": "bg-amber-400 text-black",
  "EXCLUSIVE": "bg-yellow-300 text-black",
};

function relTime(iso) {
  if (!iso) return "";
  const dt = new Date(iso);
  const diff = (Date.now() - dt.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function CategoryPill({ category, className = "" }) {
  const cat = (category || "BREAKING NEWS").toUpperCase();
  const tint = CATEGORY_TINT[cat] || "bg-white text-black";
  return (
    <span className={`inline-block px-2.5 py-1 text-[10px] font-black tracking-[0.18em] rounded-sm ${tint} ${className}`} data-testid={`news-pill-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
      {cat}
    </span>
  );
}

export default function NewsFeed() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState("");
  // Pagination — backend caps `page_size` at 50, so we paginate in
  // chunks of 30 (lead + 2 sub-leads + 27 grid cards = 30) and let the
  // user click "Load more" to append. We track total separately so we
  // can hide the button + show a "Showing X of Y" caption once the
  // newsroom has 200+ entries.
  const PAGE_SIZE = 30;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Reset pagination whenever the active category changes.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr("");
    setPage(1);
    Promise.all([
      api.get("/news", { params: { page: 1, page_size: PAGE_SIZE, category: activeCat || undefined } }),
      api.get("/news/categories").catch(() => ({ data: [] })),
    ])
      .then(([feedRes, catsRes]) => {
        if (!alive) return;
        setPosts(feedRes.data?.posts || []);
        setTotal(feedRes.data?.total || 0);
        setCategories(catsRes.data || []);
      })
      .catch((e) => alive && setErr(e?.response?.data?.detail || "Couldn't load Tap In News right now."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [activeCat]);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const next = page + 1;
      const { data } = await api.get("/news", {
        params: { page: next, page_size: PAGE_SIZE, category: activeCat || undefined },
      });
      setPosts((prev) => {
        // Defensive de-dupe — if the user creates a post mid-scroll the
        // pages can shift and the same id can come back twice.
        const seen = new Set(prev.map((p) => p.id));
        const merged = [...prev];
        for (const p of (data?.posts || [])) if (!seen.has(p.id)) merged.push(p);
        return merged;
      });
      if (typeof data?.total === "number") setTotal(data.total);
      setPage(next);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load more stories.");
    } finally {
      setLoadingMore(false);
    }
  };

  const lead = posts[0];
  const sub = posts.slice(1, 3);
  const grid = posts.slice(3);
  const hasMore = posts.length < total;

  // Infinite-scroll sentinel. We watch a small placeholder at the bottom
  // of the feed; when ≥10% of it scrolls into view, kick off the next
  // page. Falls back gracefully — the manual "Load more" button stays
  // available for keyboard users / when JS is paused. We re-bind the
  // observer whenever `loadMore`'s closure changes (page / activeCat /
  // posts) so we never fire stale loads.
  const sentinelRef = useRef(null);
  // Stash the latest loadMore in a ref so the observer callback always
  // sees the freshest closure without us re-creating the observer on
  // every render.
  const loadMoreRef = useRef(loadMore);
  useEffect(() => { loadMoreRef.current = loadMore; });
  useEffect(() => {
    if (!hasMore) return undefined;
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          loadMoreRef.current?.();
          break;
        }
      }
    }, { rootMargin: "400px 0px", threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, posts.length, activeCat]);

  return (
    <div className="min-h-screen bg-[#0a0b10] text-white pb-20" data-testid="news-feed-page">
      {/* Top breaking banner */}
      <div className="bg-red-600 text-white" data-testid="news-breaking-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-3">
          <span className="font-black tracking-widest text-xs animate-pulse flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5" /> BREAKING
          </span>
          <span className="text-xs sm:text-sm truncate opacity-90">
            Tap In News — your direct line to entertainment, music, sports, weather, and culture.
          </span>
        </div>
      </div>

      {/* Masthead */}
      <header className="border-b-4 border-red-600 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex items-end justify-between gap-4">
          <div>
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-tv-accent font-bold">
              Tap In TV Network
            </div>
            <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-1">
              <span className="text-red-500">TAP</span> IN <span className="text-tv-accent">NEWS</span>
            </h1>
            <p className="text-white/60 text-sm mt-2 max-w-xl">
              Headlines, exclusives, weather, and breaking music drops — straight from the network.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2 text-white/40">
            <Newspaper className="w-10 h-10" />
            <div className="text-[10px] uppercase tracking-[0.3em]">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
          </div>
        </div>

        {/* Category chips */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-4 overflow-x-auto" data-testid="news-categories">
          <div className="flex gap-2 min-w-max">
            <button
              onClick={() => setActiveCat("")}
              data-testid="news-cat-all"
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${!activeCat ? "bg-tv-accent text-black" : "bg-white/10 hover:bg-white/15 text-white/70"}`}
            >
              All
            </button>
            {CATEGORY_ORDER.map((c) => {
              const found = categories.find((x) => (x.category || "").toUpperCase() === c);
              const count = found?.count || 0;
              return (
                <button
                  key={c}
                  onClick={() => setActiveCat(c)}
                  data-testid={`news-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${activeCat === c ? "bg-tv-accent text-black" : "bg-white/10 hover:bg-white/15 text-white/70"}`}
                >
                  {c}{count > 0 && <span className="ml-1.5 opacity-70">{count}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {loading && (
          <div className="grid gap-6 lg:grid-cols-3" data-testid="news-loading">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} className="rounded-2xl bg-white/5 animate-pulse aspect-[4/3]" />
            ))}
          </div>
        )}

        {!loading && err && (
          <div className="text-center py-16 text-white/60" data-testid="news-error">{err}</div>
        )}

        {!loading && !err && posts.length === 0 && (
          <div className="text-center py-20 text-white/60" data-testid="news-empty">
            <Newspaper className="w-12 h-12 mx-auto opacity-40 mb-4" />
            <div className="text-lg font-semibold">No stories published yet.</div>
            <div className="text-sm mt-1">Check back soon — fresh headlines drop daily.</div>
          </div>
        )}

        {!loading && !err && lead && (
          <>
            {/* Lead story (TMZ-style hero) */}
            <Link
              to={`/tap-in-news/${lead.slug}`}
              data-testid="news-lead-card"
              className="block group rounded-2xl overflow-hidden bg-black mb-8 ring-1 ring-white/10 hover:ring-tv-accent/50 transition"
            >
              <div className="relative aspect-[16/9] sm:aspect-[21/9] bg-[#1a1a26] overflow-hidden">
                {lead.hero_image_url ? (
                  <img
                    src={lead.hero_image_url}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-red-900 via-black to-black" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8">
                  <CategoryPill category={lead.category} />
                  <h2 className="font-display font-black text-2xl sm:text-4xl lg:text-5xl mt-3 leading-tight tracking-tight">
                    <span className="bg-tv-accent text-black px-1 box-decoration-clone">
                      {lead.title}
                    </span>
                  </h2>
                  {lead.dek && (
                    <p className="text-white/85 text-sm sm:text-base mt-3 max-w-3xl line-clamp-2">{lead.dek}</p>
                  )}
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mt-3 font-bold">
                    BY {(lead.author_name || "TAP IN STAFF").toUpperCase()} · {relTime(lead.published_at || lead.created_at)}
                  </div>
                </div>
              </div>
            </Link>

            {/* Two-up sub-leads */}
            {sub.length > 0 && (
              <div className="grid gap-6 sm:grid-cols-2 mb-8" data-testid="news-sub-leads">
                {sub.map((p) => (
                  <Link
                    key={p.id}
                    to={`/tap-in-news/${p.slug}`}
                    data-testid={`news-sub-${p.slug}`}
                    className="block group rounded-xl overflow-hidden bg-black ring-1 ring-white/10 hover:ring-tv-accent/50 transition"
                  >
                    <div className="relative aspect-[16/9] bg-[#1a1a26]">
                      {p.hero_image_url ? (
                        <img src={p.hero_image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-fuchsia-900 to-black" />
                      )}
                      <div className="absolute top-3 left-3"><CategoryPill category={p.category} /></div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-display font-bold text-lg leading-snug group-hover:text-tv-accent transition-colors">
                        {p.title}
                      </h3>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-2 font-bold">
                        {(p.author_name || "Tap In Staff").toUpperCase()} · {relTime(p.published_at || p.created_at)}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* 3-col grid for the rest */}
            {grid.length > 0 && (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="news-grid">
                {grid.map((p) => (
                  <Link
                    key={p.id}
                    to={`/tap-in-news/${p.slug}`}
                    data-testid={`news-card-${p.slug}`}
                    className="group block rounded-lg overflow-hidden bg-black ring-1 ring-white/10 hover:ring-tv-accent/50 transition"
                  >
                    <div className="relative aspect-[16/10] bg-[#1a1a26]">
                      {p.hero_image_url ? (
                        <img src={p.hero_image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-indigo-900 to-black flex items-center justify-center">
                          <Newspaper className="w-8 h-8 text-white/30" />
                        </div>
                      )}
                      <div className="absolute top-2 left-2"><CategoryPill category={p.category} /></div>
                    </div>
                    <div className="p-3">
                      <h3 className="font-bold text-sm leading-snug line-clamp-3 group-hover:text-tv-accent transition-colors">
                        {p.title}
                      </h3>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-2 font-bold flex items-center gap-1">
                        {relTime(p.published_at || p.created_at)} <ChevronRight className="w-3 h-3" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Pagination footer — only renders once there's a second page
                worth of stories. Shows "Showing X of Y" and a Load-more
                button that appends the next chunk in place. */}
            {total > 0 && (
              <div
                className="mt-10 flex flex-col items-center gap-3"
                data-testid="news-pagination"
              >
                <div className="text-[11px] uppercase tracking-[0.25em] text-white/50 font-bold">
                  Showing <span className="text-white" data-testid="news-shown-count">{posts.length}</span>{" "}
                  of <span className="text-white" data-testid="news-total-count">{total}</span> stories
                </div>
                {hasMore && (
                  <>
                    <button
                      type="button"
                      onClick={loadMore}
                      disabled={loadingMore}
                      data-testid="news-load-more"
                      className="px-6 py-2.5 rounded-full bg-tv-accent text-black font-black uppercase tracking-wider text-xs hover:bg-yellow-400 disabled:opacity-60 disabled:cursor-not-allowed transition"
                    >
                      {loadingMore ? "Loading…" : "Load more headlines"}
                    </button>
                    {/* Invisible sentinel — IntersectionObserver triggers
                        loadMore() when this scrolls into view (rootMargin
                        400px lets us pre-fetch before the user hits the
                        bottom). The visible button above stays as a
                        keyboard-friendly fallback. */}
                    <div
                      ref={sentinelRef}
                      data-testid="news-infinite-sentinel"
                      aria-hidden="true"
                      className="h-2 w-full"
                    />
                  </>
                )}
                {!hasMore && posts.length >= PAGE_SIZE && (
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/30 font-bold" data-testid="news-end-of-feed">
                    — End of feed —
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
